import { useEffect, useState } from "react";

interface UseClipboardOptions {
  timeout?: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface UseClipboardReturn {
  copyToClipboard: (text: string) => Promise<boolean>;
  showCopiedToClipboard: boolean;
  isLoading: boolean;
}

export const useClipboard = (
  options: UseClipboardOptions = {}
): UseClipboardReturn => {
  const { timeout = 2000, onSuccess, onError } = options;

  const [showCopiedToClipboard, setShowCopiedToClipboard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (showCopiedToClipboard) {
      const timeoutId = setTimeout(() => {
        setShowCopiedToClipboard(false);
      }, timeout);

      return () => clearTimeout(timeoutId);
    }
  }, [showCopiedToClipboard, timeout]);

  const legacyClipboard = (text: string): void => {
    const tempInput = document.createElement("input");
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    tempInput.setSelectionRange(0, 99999); // For mobile devices
    document.execCommand("copy");
    document.body.removeChild(tempInput);
  };

  const copyToClipboard = async (text: string): Promise<boolean> => {
    setIsLoading(true);

    try {
      // Use modern clipboard API if available
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(text);
        } catch (clipboardError) {
          if (
            clipboardError instanceof Error &&
            (clipboardError.name === "NotAllowedError" ||
              clipboardError.message.includes("Document is not focused"))
          ) {
            console.log(
              "Clipboard API failed due to focus, falling back to legacy method"
            );
            legacyClipboard(text);
          } else {
            throw clipboardError;
          }
        }
      } else {
        // Fallback for older browsers or local development
        legacyClipboard(text);
      }

      setShowCopiedToClipboard(true);
      onSuccess?.();
      return true;
    } catch (error) {
      const clipboardError =
        error instanceof Error
          ? error
          : new Error("Clipboard operation failed");
      console.log("Error copying to clipboard:", clipboardError);
      onError?.(clipboardError);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    copyToClipboard,
    showCopiedToClipboard,
    isLoading,
  };
};
